import { useCallback, useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableHead from '@mui/material/TableHead';
import TableBody from '@mui/material/TableBody';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TablePagination from '@mui/material/TablePagination';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import CircularProgress from '@mui/material/CircularProgress';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import BlockIcon from '@mui/icons-material/Block';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import HelpIcon from '@mui/icons-material/Help';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import type { Role } from '../../types';
import type { AdminUser } from '../../types/admin';
import { usersApi, rolesApi } from '../../api/adminApi';
import { getApiErrorMessage } from '../../api/client';
import { notify } from '../../components/Notifier';
import { displayIdentity } from '../../utils/userIdentity';
import { UserFormDialog } from './UserFormDialog';
import { ResetPasswordDialog } from './ResetPasswordDialog';
import { ConfirmDialog } from './ConfirmDialog';
import { formatDateTime } from '../../utils/date';

export function UserManagement(): JSX.Element {
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [roles, setRoles] = useState<Role[]>([]);
  const [loading, setLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(false);
  const [formMode, setFormMode] = useState<'create' | 'edit'>('create');
  const [editingUser, setEditingUser] = useState<AdminUser | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<AdminUser | null>(null);
  const [resetPasswordTarget, setResetPasswordTarget] = useState<AdminUser | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [togglingId, setTogglingId] = useState<string | null>(null);
  const [togglingTourId, setTogglingTourId] = useState<string | null>(null);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [userPage, rolePage] = await Promise.all([
        usersApi.list({ limit: rowsPerPage, offset: page * rowsPerPage }),
        rolesApi.list({ limit: 200 }),
      ]);
      setUsers(userPage.items);
      setTotal(userPage.total);
      setRoles(rolePage.items);
    } catch (err) {
      notify(getApiErrorMessage(err, 'Không thể tải danh sách người dùng'), 'error');
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage]);

  useEffect(() => {
    void load();
  }, [load]);

  const openCreate = () => {
    setFormMode('create');
    setEditingUser(null);
    setFormOpen(true);
  };

  const openEdit = (user: AdminUser) => {
    setFormMode('edit');
    setEditingUser(user);
    setFormOpen(true);
  };

  const handleToggleActive = async (user: AdminUser) => {
    setTogglingId(user.id);
    try {
      await usersApi.setActive(user.id, user.is_active === 0);
      notify(user.is_active === 0 ? 'Đã kích hoạt người dùng' : 'Đã vô hiệu hóa người dùng', 'success');
      await load();
    } catch (err) {
      notify(getApiErrorMessage(err, 'Không thể thay đổi trạng thái'), 'error');
    } finally {
      setTogglingId(null);
    }
  };

  const handleToggleTour = async (user: AdminUser) => {
    setTogglingTourId(user.id);
    try {
      await usersApi.setOnboardingTour(user.id, user.show_onboarding_tour === 0);
      notify(
        user.show_onboarding_tour === 0 ? 'Đã bật hướng dẫn sử dụng cho người dùng' : 'Đã tắt hướng dẫn sử dụng',
        'success',
      );
      await load();
    } catch (err) {
      notify(getApiErrorMessage(err, 'Không thể thay đổi trạng thái hướng dẫn'), 'error');
    } finally {
      setTogglingTourId(null);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await usersApi.remove(deleteTarget.id);
      notify('Đã xóa mềm người dùng', 'success');
      setDeleteTarget(null);
      await load();
    } catch (err) {
      notify(getApiErrorMessage(err, 'Không thể xóa người dùng'), 'error');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="h5" fontWeight={700}>
          Quản lý người dùng
        </Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
          Thêm người dùng
        </Button>
      </Stack>

      <Paper variant="outlined">
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Họ tên</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Vai trò</TableCell>
                <TableCell>Trạng thái</TableCell>
                <TableCell>Đăng nhập gần nhất</TableCell>
                <TableCell align="right">Hành động</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                    <CircularProgress size={24} />
                  </TableCell>
                </TableRow>
              )}
              {!loading && users.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                    Chưa có người dùng nào.
                  </TableCell>
                </TableRow>
              )}
              {!loading &&
                users.map((user) => (
                  <TableRow key={user.id} hover>
                    <TableCell>{user.name}</TableCell>
                    <TableCell>{displayIdentity(user)}</TableCell>
                    <TableCell>
                      <Stack direction="row" flexWrap="wrap" gap={0.5}>
                        {(user.roles ?? []).length === 0 && (
                          <Typography variant="caption" color="text.secondary">
                            Chưa có vai trò
                          </Typography>
                        )}
                        {(user.roles ?? []).map((role) => (
                          <Chip key={role.id} label={role.name} size="small" />
                        ))}
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={user.is_active ? 'Hoạt động' : 'Ngừng hoạt động'}
                        color={user.is_active ? 'success' : 'default'}
                        size="small"
                        variant={user.is_active ? 'filled' : 'outlined'}
                      />
                    </TableCell>
                    <TableCell>{formatDateTime(user.last_login)}</TableCell>
                    <TableCell align="right">
                      <Tooltip title="Sửa">
                        <IconButton size="small" onClick={() => openEdit(user)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={user.is_active ? 'Vô hiệu hóa' : 'Kích hoạt'}>
                        <span>
                          <IconButton
                            size="small"
                            onClick={() => void handleToggleActive(user)}
                            disabled={togglingId === user.id}
                          >
                            {user.is_active ? (
                              <BlockIcon fontSize="small" />
                            ) : (
                              <CheckCircleIcon fontSize="small" />
                            )}
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip
                        title={
                          user.show_onboarding_tour
                            ? 'Đang bật hướng dẫn sử dụng — bấm để tắt'
                            : 'Bật hướng dẫn sử dụng (onboarding tour)'
                        }
                      >
                        <span>
                          <IconButton
                            size="small"
                            onClick={() => void handleToggleTour(user)}
                            disabled={togglingTourId === user.id}
                          >
                            {user.show_onboarding_tour ? (
                              <HelpIcon fontSize="small" color="primary" />
                            ) : (
                              <HelpOutlineIcon fontSize="small" />
                            )}
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title="Đặt lại mật khẩu">
                        <IconButton size="small" onClick={() => setResetPasswordTarget(user)}>
                          <VpnKeyIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Xóa mềm">
                        <IconButton size="small" onClick={() => setDeleteTarget(user)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(Number(e.target.value));
            setPage(0);
          }}
          rowsPerPageOptions={[10, 25, 50, 100]}
          labelRowsPerPage="Số dòng/trang"
          labelDisplayedRows={({ from, to, count }) => `${from}–${to} / ${count}`}
        />
      </Paper>

      <UserFormDialog
        open={formOpen}
        mode={formMode}
        user={editingUser}
        roles={roles}
        onClose={() => setFormOpen(false)}
        onSaved={() => void load()}
      />

      <ResetPasswordDialog
        open={resetPasswordTarget !== null}
        user={resetPasswordTarget}
        onClose={() => setResetPasswordTarget(null)}
      />

      <ConfirmDialog
        open={deleteTarget !== null}
        title="Xóa mềm người dùng"
        message={`Người dùng "${deleteTarget?.name}" sẽ bị ẩn khỏi hệ thống (xóa mềm). Bạn có chắc chắn muốn tiếp tục?`}
        confirmLabel="Xóa mềm"
        loading={deleting}
        onConfirm={() => void handleDelete()}
        onCancel={() => setDeleteTarget(null)}
      />
    </Box>
  );
}
